import React, { useState, useEffect } from 'react';
import { FaTimes, FaSpinner, FaCheckCircle, FaExternalLinkAlt, FaShieldAlt } from 'react-icons/fa';
import { SiZerodha } from 'react-icons/si';
import api from '../api';
import ZerodhaBrokerErrorAlert from './ZerodhaBrokerErrorAlert';
import { getBrokerErrorMessage, isZerodhaApiKeyRejectedError } from '../utils/zerodhaBrokerErrors';

const ZerodhaConnectModal = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: Credentials, 2: Login, 3: Processing
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastError, setLastError] = useState(null);
  const [zerodhaWindow, setZerodhaWindow] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setStep(1);
      setApiKey('');
      setApiSecret('');
      setError('');
      setLastError(null);
      setLoading(false);
      if (zerodhaWindow && !zerodhaWindow.closed) {
        zerodhaWindow.close();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    // Listen for messages from the OAuth callback
    const handleMessage = (event) => {
      // Verify origin for security
      if (event.origin !== window.location.origin) return;
      
      if (event.data.type === 'ZERODHA_AUTH_SUCCESS') {
        // Connection successful
        if (zerodhaWindow && !zerodhaWindow.closed) {
          zerodhaWindow.close();
        }
        onSuccess();
      } else if (event.data.type === 'ZERODHA_AUTH_ERROR') {
        setLastError(null);
        setError(event.data.error || 'Authentication failed');
        setStep(1);
        setLoading(false);
        if (zerodhaWindow && !zerodhaWindow.closed) {
          zerodhaWindow.close();
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [zerodhaWindow, onSuccess]);

  const handleSaveCredentials = async (e) => {
    e.preventDefault();
    setError('');
    setLastError(null);
    
    if (!apiKey.trim() || !apiSecret.trim()) {
      setError('Please enter both API Key and API Secret');
      return;
    }

    try {
      setLoading(true);
      
      // Step 1: Save credentials to database (encrypted)
      await api.post('/api/v1/brokers/zerodha/credentials', {
        api_key: apiKey,
        api_secret: apiSecret
      });
      
      // Move to step 2 (show "Login with Zerodha" button)
      setStep(2);
    } catch (err) {
      setLastError(err);
      setError(getBrokerErrorMessage(err, 'Failed to save credentials. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const openZerodhaOAuthPopup = () => {
    setStep(3);
    setLoading(true);

    sessionStorage.setItem('zerodha_api_key', apiKey);
    sessionStorage.setItem('zerodha_api_secret', apiSecret);

    const width = 600;
    const height = 700;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    const oauthStartUrl = `${window.location.origin}/brokers/zerodha/oauth-start`;

    const popup = window.open(
      oauthStartUrl,
      'Zerodha Login',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
    );

    if (!popup) {
      setLoading(false);
      setStep(2);
      setError('Failed to open popup. Please allow popups for this site.');
      return;
    }

    setZerodhaWindow(popup);
    
    // Poll to check if popup is closed
    const pollTimer = setInterval(() => {
      if (popup && popup.closed) {
        clearInterval(pollTimer);
        setLoading(false);
        // Check if we didn't receive success message
        if (step === 3) {
          setError('Login window was closed. Please try again.');
          setStep(2);
        }
      }
    }, 500);
  };

  const handleGenerateLoginUrl = async () => {
    setError('');
    setLastError(null);

    try {
      setLoading(true);
      await api.get('/api/v1/brokers/zerodha/login-url');
      openZerodhaOAuthPopup();
    } catch (err) {
      setLastError(err);
      setError(
        getBrokerErrorMessage(
          err,
          'Failed to generate login URL. Please ensure your credentials are saved.'
        )
      );
      setStep(2);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <SiZerodha className="text-3xl text-blue-600" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Connect Zerodha Account</h2>
              <p className="text-sm text-gray-600">Step {step} of 3</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FaTimes className="text-2xl" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            isZerodhaApiKeyRejectedError(lastError) ? (
              <ZerodhaBrokerErrorAlert message={error} err={lastError} className="mb-4" />
            ) : (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                {error}
              </div>
            )
          )}

          {/* Step 1: API Credentials */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2 flex items-center">
                  <FaShieldAlt className="mr-2" />
                  Getting Started
                </h3>
                <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
                  <li>Visit <a href="https://kite.trade" target="_blank" rel="noopener noreferrer" className="underline font-medium">kite.trade</a> and create a Kite Connect app</li>
                  <li>Copy your API Key and API Secret</li>
                  <li>Paste them below to connect your account</li>
                </ol>
              </div>

              <form onSubmit={handleSaveCredentials} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    API Key *
                  </label>
                  <input
                    type="text"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your Zerodha API Key"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    API Secret *
                  </label>
                  <input
                    type="password"
                    value={apiSecret}
                    onChange={(e) => setApiSecret(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your Zerodha API Secret"
                    required
                  />
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    <strong>⚠️ Security Note:</strong> Your credentials are encrypted before storage. 
                    We never store your trading password.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <FaSpinner className="animate-spin mr-2" />
                      Saving Credentials...
                    </>
                  ) : (
                    <>
                      Save Credentials & Continue
                    </>
                  )}
                </button>
              </form>
            </div>
          )}

          {/* Step 2: Credentials Saved - Ready to Login */}
          {step === 2 && (
            <div className="space-y-6 text-center">
              <div className="flex justify-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                  <FaCheckCircle className="text-4xl text-green-600" />
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Credentials Saved Successfully!</h3>
                <p className="text-gray-600">
                  Your API credentials have been securely encrypted and saved. 
                  Now click the button below to login with Zerodha and complete the connection.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
                <h4 className="font-semibold text-blue-900 mb-2">What happens next?</h4>
                <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                  <li>A popup window will open with Zerodha's login page</li>
                  <li>Log in with your Zerodha credentials</li>
                  <li>Authorize TrendVector to access your account</li>
                  <li>The popup will close automatically</li>
                  <li>Your account will be connected!</li>
                </ol>
              </div>

              <button
                onClick={handleGenerateLoginUrl}
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <FaSpinner className="animate-spin mr-2" />
                    Opening Login...
                  </>
                ) : (
                  <>
                    Login with Zerodha
                    <FaExternalLinkAlt className="ml-2" />
                  </>
                )}
              </button>

              <button
                onClick={() => setStep(1)}
                disabled={loading}
                className="w-full text-gray-600 hover:text-gray-800 text-sm disabled:opacity-50"
              >
                ← Back to credentials
              </button>
            </div>
          )}

          {/* Step 3: Processing */}
          {step === 3 && (
            <div className="space-y-6 text-center py-8">
              <div className="flex justify-center">
                <FaSpinner className="animate-spin text-6xl text-blue-600" />
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Connecting Your Account...</h3>
                <p className="text-gray-600">
                  Please complete the login in the popup window.
                  This window will update automatically once you're connected.
                </p>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  If the popup was blocked, please allow popups for this site and try again.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <p className="text-xs text-gray-600 text-center">
            By connecting your account, you agree to allow TrendVector to place orders on your behalf.
            You can disconnect at any time from your settings.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ZerodhaConnectModal;

